<template>
  <div
    class="min-h-screen h-full bg-white md:bg-[linear-gradient(143.08deg,#F7FBFF_21.45%,#EEF7FF_78.55%)]"
  >
    <TsHeader data-testid="header" />
    <TsSection append-class="mb-0 pb-12">
      <div>
        <slot />
      </div>
    </TsSection>

    <TsFooter data-testid="footer" data-fetch-key="product-layout-footer-links" />
  </div>
  <ClientOnly>
    <Teleport to="#teleports">
      <template v-if="isMobile">
        <TsDrawer
          v-model:visible="productStore.subscribe_notification_modal_visible"
          data-testid="subscribe-notification-drawer-mobile"
          position="bottom"
          append-class="bg-transparent overflow-hidden min-h-48"
        >
          <TsModalOutOfStockSubscription
            @close="productStore.subscribe_notification_modal_visible = false"
            @subscribe="
              productStore.subscribe_notification_modal_visible = false
            "
          />
        </TsDrawer>
      </template>

      <template v-if="isDesktop">
        <TsModal
          v-model:visible="productStore.subscribe_notification_modal_visible"
          data-testid="subscribe-notification-modal"
          append-class="max-w-[450px]"
        >
          <template #modal-content>
            <TsModalOutOfStockSubscription
              @close="productStore.subscribe_notification_modal_visible = false"
              @subscribe="
                productStore.subscribe_notification_modal_visible = false
              "
            />
          </template>
        </TsModal>
      </template>
      <template v-if="isDesktop">
        <TsModal
          v-model:visible="productStore.subscription_successful_modal_visible"
          data-testid="subscription-successful"
          append-class="max-w-[450px]"
        >
          <template #modal-content>
            <TsModalSubscriptionConfirmation
              :productName="productStore.notify_product_name"
              :email="productStore.notify_user_email"
              @close="
                productStore.subscription_successful_modal_visible = false
              "
            />
          </template>
        </TsModal>
      </template>

      <template v-if="isMobile">
        <TsDrawer
          v-model:visible="productStore.subscription_successful_modal_visible"
          data-testid="subscription-successful-mobile"
          position="bottom"
          append-class="bg-transparent overflow-hidden min-h-48"
        >
          <TsModalSubscriptionConfirmation
            :productName="productStore.notify_product_name"
            :email="productStore.notify_user_email"
            @close="productStore.subscription_successful_modal_visible = false"
          />
        </TsDrawer>
      </template>

      <template v-if="isDesktop">
        <TsModal
          v-model:visible="trolleyStore.already_subscribed_modal_visible"
          data-testid="already-subscribed"
          append-class="max-w-[450px]"
        >
          <template #modal-content>
            <TsModalSubscriptionConfirmation
              variant="alreadySubscribed"
              @close="trolleyStore.already_subscribed_modal_visible = false"
            />
          </template>
        </TsModal>
      </template>

      <template v-if="isMobile">
        <TsDrawer
          v-model:visible="trolleyStore.already_subscribed_modal_visible"
          data-testid="already-subscribed-mobile"
          position="bottom"
          append-class="bg-transparent overflow-hidden min-h-48"
        >
          <TsModalSubscriptionConfirmation
            variant="alreadySubscribed"
            @close="trolleyStore.already_subscribed_modal_visible = false"
          />
        </TsDrawer>
      </template>

      <TsFindStockNearbyActions
        v-if="productStore.product"
        :product="productStore.product"
        data-testid="PDP-nearby-stock-finder"
        @post-add="
          (quantityAdded, trolleyChannel) =>
            onAddToCollection(quantityAdded, trolleyChannel)
        "
      />

      <TsModalSaveListFormV2 />

      <template v-if="isMobile"> 
        <TsDrawer
          data-testid="pdp-next-day-collection-more-info"
          v-model:visible="productStore.isNextDayCollectionInfoModalVisible"
          position="bottom"
          append-class="bg-white overflow-hidden"
        >
          <TsModalNextDayCollectionInfo
            @close="productStore.isNextDayCollectionInfoModalVisible = false"
          />
        </TsDrawer>
      </template>
      <template v-else-if="isDesktop">
        <TsModal
          data-testid="pdp-next-day-collection-more-info"
          v-model:visible="productStore.isNextDayCollectionInfoModalVisible"
          position="bottom"
          append-class="bg-white overflow-hidden max-w-[558px]"
        >
        <template #modal-content>
            <TsModalNextDayCollectionInfo
              @close="productStore.isNextDayCollectionInfoModalVisible = false"
            />
        </template>
        </TsModal>
      </template>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
const savelistStore = useSavelistStore();

import { TrolleyChannel } from "~/types/ecom/trolley/trolley-map";

const TsHeader = defineAsyncComponent(
  () => import("~/components/TsHeader.vue")
);
const TsFooter = defineAsyncComponent(
  () => import("~/components/TsFooter.vue")
);
const TsDrawer = defineAsyncComponent(
  () => import("~/components/organisms/TsDrawer.vue")
);
const TsModalSaveListFormV2 = defineAsyncComponent(
  () => import("~/components/organisms/TsModalSaveListFormV2.vue")
);
const TsModalOutOfStockSubscription = defineAsyncComponent(
  () => import("~/components/organisms/TsModalOutOfStockSubscription.vue")
);
const TsModalSubscriptionConfirmation = defineAsyncComponent(
  () => import("~/components/organisms/TsModalSubscriptionConfirmation.vue")
);
const TsFindStockNearbyActions = defineAsyncComponent(
  () => import("~/components/organisms/TsFindStockNearbyActions.vue")
);

const TsModalNextDayCollectionInfo = defineAsyncComponent(
  () => import("~/components/organisms/TsModalNextDayCollectionInfo.vue")
);

const productStore = useProductStore();
const trolleyStore = useTrolleyStore();

const { isMobile, isDesktop } = useDevice();

let addToCollectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

function onAddToCollection(
  quantityAdded: number,
  trolleyChannel: TrolleyChannel
) {
  productStore.quantity_to_add = quantityAdded;
  productStore.delivery_method =
    trolleyChannel === TrolleyChannel.Collection
      ? "Collection"
      : "NextDayCollection";

  if (addToCollectionTimeoutId) {
    clearTimeout(addToCollectionTimeoutId);
  }

  addToCollectionTimeoutId = setTimeout(() => {
    useStockStore().stock_finder_modal_visible = false;
    productStore.is_confirmation_modal_visible = true;
  }, 700);
}

onUnmounted(() => {
  if (addToCollectionTimeoutId) {
    clearTimeout(addToCollectionTimeoutId);
    addToCollectionTimeoutId = null;
  }
});
</script>
